import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../../utils/api'
import { toast } from 'react-toastify';
import { useAccountContext } from '../AdminAccountPage';
import { AdminAccountInput, EmailDomain, BrandProfile } from '../types';
import { useSortableData, SortableHeader, SortDirection } from '../../components/SortableTable';

// ========================================
// Styled Components - Matching Semantic UI
// ========================================

const FormSection = styled.div`
  margin-bottom: 24px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div<{ $fullWidth?: boolean }>`
  grid-column: ${({ $fullWidth }) => ($fullWidth ? '1 / -1' : 'auto')};
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.87);
  margin-bottom: 4px;
  
  &.required::after {
    content: ' *';
    color: #db2828;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    outline: none;
    border-color: #85b7d9;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid rgba(34, 36, 38, 0.15);
  border-radius: 4px;
  font-size: 14px;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #85b7d9;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  cursor: pointer;
  text-transform: uppercase;
  font-weight: 600;
  
  input {
    margin-right: 8px;
    width: 17px;
    height: 17px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 16px;
`;

const Button = styled.button<{ $primary?: boolean }>`
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  
  ${({ $primary }) => $primary ? `
    background: #2185d0;
    color: white;
    &:hover { background: #1678c2; }
  ` : `
    background: #e0e1e2;
    color: rgba(0, 0, 0, 0.6);
    &:hover { background: #cacbcd; }
  `}
  
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const SectionHeader = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 32px 0 16px 0;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(34, 36, 38, 0.15);
`;

const PublicationsHeader = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  margin: 32px 0 8px 0;
  border-bottom: 1px solid rgba(34, 36, 38, 0.15);
  padding-bottom: 8px;
`;

const SubHeader = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border: 1px solid rgba(34, 36, 38, 0.15);
  margin-top: 16px;
  
  th, td {
    text-align: left;
    padding: 11px 12px;
    border-top: 1px solid rgba(34, 36, 38, 0.1);
  }
  
  th {
    background: #f9fafb;
    font-size: 12px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.87);
    border-top: none;
  }
  
  tr:hover td {
    background: #f9fafb;
  }
`;

const ActionLink = styled(Link)`
  color: #4183c4;
  text-decoration: none;
  margin-right: 8px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ActionButton = styled.button`
  padding: 6px 12px;
  background: #e0e1e2;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  
  &:hover {
    background: #cacbcd;
  }
`;

const DeleteButton = styled.button`
  padding: 6px 12px;
  background: #db2828;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 13px;
  cursor: pointer;
  
  &:hover {
    background: #ca1010;
  }
  
  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const InlineFormRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
  margin-bottom: 16px;
`;

const ActionIcon = styled.button`
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  color: #374151;
  width: 28px;
  height: 28px;
  cursor: pointer;
  margin-right: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  
  &:hover {
    background: #f3f4f6;
  }
`;

const AvatarCell = styled.td`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  background: #e0e1e2;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #767676;
`;

const SortableHeaderCell = styled.th<{ $sortable?: boolean; $sorted?: boolean }>`
  background: #f9fafb;
  font-size: 12px;
  font-weight: 700;
  color: ${({ $sorted }) => ($sorted ? '#00a6c2' : 'rgba(0, 0, 0, 0.87)')};
  border-top: none;
  cursor: ${({ $sortable }) => ($sortable ? 'pointer' : 'default')};
  user-select: none;
  white-space: nowrap;
  
  &:hover {
    ${({ $sortable }) => $sortable && `
      background: #f0f0f0;
      color: #00a6c2;
    `}
  }
`;

const SortIndicator = styled.span<{ $direction: SortDirection }>`
  margin-left: 4px;
  display: inline-block;
  
  &::after {
    content: '${({ $direction }) => {
      if ($direction === 'asc') return '↓';
      if ($direction === 'desc') return '↑';
      return '';
    }}';
    font-size: 12px;
  }
`;

// ========================================
// Component
// ========================================

function AccountInfoTab() {
  const navigate = useNavigate();
  const { account, industries, employees, refetchAccount } = useAccountContext();
  
  const [formData, setFormData] = useState<Partial<AdminAccountInput>>({});
  const [emailDomains, setEmailDomains] = useState<EmailDomain[]>([]);
  const [publications, setPublications] = useState<BrandProfile[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [saving, setSaving] = useState(false);
  const [deletingDomain, setDeletingDomain] = useState<string | null>(null);
  
  // Sorting for publications table
  const { sortedData: sortedPublications, requestSort, getSortDirection } = useSortableData(publications);

  // Initialize form data from account
  useEffect(() => {
    if (account) {
      setFormData({
        id: account.id,
        name: account.name,
        salesforceId: account.salesforceId || '',
        industryId: account.industryId || '',
        accountManagerId: account.accountManagerId || '',
        programManagerId: account.programManagerId || '',
        expireSessionOnClose: account.expireSessionOnClose || false,
        requireMfa: account.requireMfa || false,
        passwordArchivingCount: account.passwordArchivingCount || 0,
        passwordExpirationInDays: account.passwordExpirationInDays || 0,
        sessionTimeoutMinutes: account.sessionTimeoutMinutes || 0,
        signoutRedirectUrl: account.signoutRedirectUrl || '',
      });
      
      // Use email domains and publications from account response
      setEmailDomains(account.emailDomains || []);
      setPublications(account.brandProfiles || []);
    }
  }, [account]);

  const refetchEmailDomains = async () => {
    // Refetch account to get updated email domains
    await refetchAccount();
  };

  const handleChange = (field: keyof AdminAccountInput, value: string | number | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const mfaJustEnabled = formData.requireMfa && !account?.requireMfa;
    if (mfaJustEnabled) {
      const confirmed = window.confirm(
        'Enabling MFA will require all users in this account (including you) to set up two-factor authentication. ' +
        'You will be redirected to enroll after saving. Continue?'
      );
      if (!confirmed) return;
    }

    setSaving(true);
    
    try {
      const payload = {
        account: {
          name: formData.name,
          salesforce_id: formData.salesforceId,
          industry_id: formData.industryId || null,
          account_manager_id: formData.accountManagerId || null,
          program_manager_id: formData.programManagerId || null,
          expire_session_on_close: formData.expireSessionOnClose,
          require_mfa: formData.requireMfa,
          password_archiving_count: formData.passwordArchivingCount ?? 0,
          password_expiration_in_days: formData.passwordExpirationInDays ?? 0,
          session_timeout_minutes: formData.sessionTimeoutMinutes ? formData.sessionTimeoutMinutes : null,
          signout_redirect_url: formData.signoutRedirectUrl,
        },
      };

      const response = await api.put(`/admin2/accounts/${account?.id}.json`, payload);
      
      if (response.data?.success) {
        if (mfaJustEnabled) {
          toast.success('MFA enabled. Redirecting to set up your authenticator...');
          setTimeout(() => { window.location.href = '/account/mfa'; }, 1500);
        } else {
          toast.success('Account updated successfully');
          await refetchAccount();
        }
      } else {
        toast.error(response.data?.errors?.join(', ') || 'Failed to save account');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.errors?.join(', ') || 'Failed to save account');
    }
    
    setSaving(false);
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim() || !account) return;
    
    try {
      await api.post('/admin2/email_domains.json', {
        email_domain: {
          name: newDomain.trim(),
          account_id: account.id,
        },
      });
      
      toast.success('Email domain added');
      setNewDomain('');
      await refetchAccount();
    } catch (error: any) {
      toast.error(error.response?.data?.errors?.join(', ') || 'Failed to add email domain');
    }
  };

  const handleDeleteDomain = async (domainId: string) => {
    if (!account) return;
    
    setDeletingDomain(domainId);
    try {
      await api.delete(`/admin2/email_domains/${domainId}.json?account_id=${account.id}`);
      toast.success('Email domain deleted');
      await refetchAccount();
    } catch (error: any) {
      toast.error(error.response?.data?.errors?.join(', ') || 'Failed to delete email domain');
    }
    setDeletingDomain(null);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-CA'); // YYYY-MM-DD format
  };

  if (!account) return null;

  return (
    <form onSubmit={handleSubmit}>
      {/* Account Information Form */}
      <FormSection>
        <FormGrid>
          <FormGroup>
            <Label className="required">Name</Label>
            <Input
              type="text"
              value={formData.name || ''}
              onChange={e => handleChange('name', e.target.value)}
              required
            />
          </FormGroup>
          
          <FormGroup>
            <Label>SalesForce Parent Account ID</Label>
            <Input
              type="text"
              value={formData.salesforceId || ''}
              onChange={e => handleChange('salesforceId', e.target.value)}
            />
          </FormGroup>
          
          <FormGroup>
            <Label className="required">Account manager</Label>
            <Select 
              value={formData.accountManagerId || ''} 
              onChange={e => handleChange('accountManagerId', e.target.value)}
              required
            >
              <option value="">Select an account manager</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.fullName || emp.email}
                </option>
              ))}
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label>Program manager</Label>
            <Select 
              value={formData.programManagerId || ''} 
              onChange={e => handleChange('programManagerId', e.target.value)}
            >
              <option value="">Select a program manager</option>
              {employees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.fullName || emp.email}
                </option>
              ))}
            </Select>
          </FormGroup>
          
          <FormGroup>
            <Label>Industry</Label>
            <Select 
              value={formData.industryId || ''} 
              onChange={e => handleChange('industryId', e.target.value)}
            >
              <option value="">Select one</option>
              {industries.map(ind => (
                <option key={ind.id} value={ind.id}>{ind.name}</option>
              ))}
            </Select>
          </FormGroup>
        </FormGrid>

        {/* Security Settings - only shown for existing accounts */}
        <FormGrid style={{ marginTop: 20 }}>
          <FormGroup>
            <Label>Session timeout (in minutes)</Label>
            <Input
              type="number"
              min="0"
              value={formData.sessionTimeoutMinutes || ''}
              onChange={e => handleChange('sessionTimeoutMinutes', parseInt(e.target.value) || 0)}
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Password expiration (in days)</Label>
            <Input
              type="number"
              min="0"
              value={formData.passwordExpirationInDays || ''}
              onChange={e => handleChange('passwordExpirationInDays', parseInt(e.target.value) || 0)}
            />
          </FormGroup>
          
          <FormGroup>
            <Label>Password reuse policy (number of passwords)</Label>
            <Input
              type="number"
              min="0"
              value={formData.passwordArchivingCount || ''}
              onChange={e => handleChange('passwordArchivingCount', parseInt(e.target.value) || 0)}
            />
          </FormGroup>
          
          <FormGroup>
            <Label>After signout redirect URL</Label>
            <Input
              type="url"
              value={formData.signoutRedirectUrl || ''}
              onChange={e => handleChange('signoutRedirectUrl', e.target.value)}
            />
          </FormGroup>
          
          <FormGroup $fullWidth>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.expireSessionOnClose || false}
                onChange={e => handleChange('expireSessionOnClose', e.target.checked)}
              />
              Log users out after closing browser
            </CheckboxLabel>
          </FormGroup>

          <FormGroup $fullWidth>
            <CheckboxLabel>
              <input
                type="checkbox"
                checked={formData.requireMfa || false}
                onChange={e => handleChange('requireMfa', e.target.checked)}
              />
              Require Multi-Factor Authentication (MFA)
            </CheckboxLabel>
          </FormGroup>
        </FormGrid>

        <ButtonGroup>
          <Button type="button" onClick={() => navigate('/admin2/accounts')}>
            Cancel
          </Button>
          <Button type="submit" $primary disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </ButtonGroup>
      </FormSection>

      {/* Email Domains Section */}
      <SectionHeader>Email domains</SectionHeader>
      <InlineFormRow>
        <FormGroup>
          <Label>Domain name</Label>
          <Input
            type="text"
            value={newDomain}
            onChange={e => setNewDomain(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), handleAddDomain())}
            placeholder="Enter a domain name, example: 'chase.com'"
            style={{ width: 400 }}
          />
        </FormGroup>
        <Button type="button" onClick={handleAddDomain}>Add</Button>
      </InlineFormRow>

      {emailDomains.length > 0 && (
        <Table>
          <thead>
            <tr>
              <th>Id</th>
              <th>Domain</th>
              <th>Created at</th>
              <th>Created by</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {emailDomains.map(domain => (
              <tr key={domain.id}>
                <td>{domain.id}</td>
                <td>{domain.name}</td>
                <td>{formatDate(domain.createdAt)}</td>
                <td>{domain.createdBy || '-'}</td>
                <td>
                  <DeleteButton
                    type="button"
                    onClick={() => handleDeleteDomain(domain.id)}
                    disabled={deletingDomain === domain.id}
                  >
                    {deletingDomain === domain.id ? 'Deleting...' : 'Delete'}
                  </DeleteButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Publications Section */}
      <PublicationsHeader>
        Publications
        <SubHeader>
          {publications.length} TOTAL - {publications.filter(p => p.active).length} ACTIVE
        </SubHeader>
      </PublicationsHeader>
      
      <ActionLink to={`/admin2/accounts/${account.id}/clone_brand_profile`}>
        Clone publication
      </ActionLink>

      <Table>
        <thead>
          <tr>
            <SortableHeaderCell
              $sortable
              $sorted={getSortDirection('id') !== null}
              onClick={() => requestSort('id')}
            >
              ID
              <SortIndicator $direction={getSortDirection('id')} />
            </SortableHeaderCell>
            <SortableHeaderCell
              $sortable
              $sorted={getSortDirection('name') !== null}
              onClick={() => requestSort('name')}
            >
              Name
              <SortIndicator $direction={getSortDirection('name')} />
            </SortableHeaderCell>
            <SortableHeaderCell
              $sortable
              $sorted={getSortDirection('createdAt') !== null}
              onClick={() => requestSort('createdAt')}
            >
              Created at
              <SortIndicator $direction={getSortDirection('createdAt')} />
            </SortableHeaderCell>
            <SortableHeaderCell
              $sortable
              $sorted={getSortDirection('active') !== null}
              onClick={() => requestSort('active')}
            >
              Active
              <SortIndicator $direction={getSortDirection('active')} />
            </SortableHeaderCell>
            <SortableHeaderCell
              $sortable
              $sorted={getSortDirection('internal') !== null}
              onClick={() => requestSort('internal')}
            >
              Internal
              <SortIndicator $direction={getSortDirection('internal')} />
            </SortableHeaderCell>
            <SortableHeaderCell
              $sortable
              $sorted={getSortDirection('billingTypeHuman') !== null}
              onClick={() => requestSort('billingTypeHuman')}
            >
              Billing Type
              <SortIndicator $direction={getSortDirection('billingTypeHuman')} />
            </SortableHeaderCell>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedPublications.map(pub => (
            <tr key={pub.id}>
              <td>{pub.id}</td>
              <AvatarCell>
                <Avatar>
                  {pub.name?.charAt(0)?.toUpperCase() || 'P'}
                </Avatar>
                <ActionLink to={`/admin2/publications/${pub.id}/edit`}>
                  {pub.name}
                </ActionLink>
              </AvatarCell>
              <td>{formatDate(pub.createdAt)}</td>
              <td>{pub.active ? 'true' : 'false'}</td>
              <td>{pub.internal ? 'true' : 'false'}</td>
              <td>{pub.billingTypeHuman || pub.billingType || 'Invoice'}</td>
              <td>
                <ActionIcon 
                  type="button" 
                  title="Edit publication"
                  onClick={() => navigate(`/admin2/publications/${pub.id}/edit`)}
                >
                  ✏️
                </ActionIcon>
                <ActionIcon 
                  type="button" 
                  title="Edit publication teams"
                  onClick={() => window.location.href = `/publications/${pub.id}/users/teams`}
                >
                  👥
                </ActionIcon>
                <ActionIcon 
                  type="button" 
                  title="Edit publication in platform"
                  onClick={() => window.location.href = `/publications/${pub.id}/content_strategy/report`}
                >
                  ⚙️
                </ActionIcon>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </form>
  );
}

export default AccountInfoTab;
